'use client'

import useInView from '@/lib/hooks/useInView'
import { pauseAndMuteOtherMedia } from '@/lib/utils'
import { useCallback, useEffect, useRef, useState } from 'react'

type AudioPlayerProps = {
	audio: string
	heading?: string
	text?: string
}

const AudioPlayer = ({ audio, heading, text }: AudioPlayerProps) => {

	const [containerRef, inView] = useInView()
	const [timeProgress, setTimeProgress] = useState<number>(0)
	const [duration, setDuration] = useState<number>(0)

	const audioRef = useRef<HTMLAudioElement>(null)

	const [isPlaying, setIsPlaying] = useState<boolean>(false)

	const togglePlayPause = () => {
		if (!audioRef.current) return

		if (isPlaying) {
			audioRef.current.pause()
			audioRef.current.muted = true
		} else {
			pauseAndMuteOtherMedia(audioRef.current)
			audioRef.current.play()
			audioRef.current.muted = false
		}
	}

	const playHandler = () => {
		console.log(`${heading || "audio"} is playing`)
		setIsPlaying(true)
	}

	const pauseHandler = () => {
		console.log(`${heading || "audio"} is paused`)
		setIsPlaying(false)
	}

	const endedHandler = () => {
		console.log(`${heading || "audio"} has ended`)
		setIsPlaying(false)
	}

	const playAnimationRef = useRef<any>(null)

	const repeat = useCallback(() => {
		if (!audioRef.current) return

		const currentTime = audioRef.current.currentTime
		setTimeProgress(currentTime)
		playAnimationRef.current = requestAnimationFrame(repeat)
	}, [audioRef.current, duration])

	useEffect(() => {
		if (!audioRef.current) return

		playAnimationRef.current = requestAnimationFrame(repeat)
	}, [])

	useEffect(() => {
		if (!audioRef.current) return

		const seconds = audioRef.current.duration
		setDuration(seconds)
	}, [audioRef?.current])

	useEffect(() => {
		if (!audioRef.current) return

		if (!audioRef?.current?.paused) {
			audioRef.current.pause()
		}
	}, [inView])

	return (
		<>

			<div ref={containerRef} className={'relative w-full aspect-square'}>

				<audio
					src={audio}
					ref={audioRef}
					preload='auto'
					className='invisible'
					onPlay={playHandler}
					onPause={pauseHandler}
					onEnded={endedHandler}
				/>

				{heading && <h2 className="style-h3 mb-4">{heading}</h2>}

				{text && <h2 className="style-body">{text}</h2>}

				<div className="absolute bottom-0 left-0">
					<button className='w-10 h-10 block' onClick={togglePlayPause}>
						<svg className={'w-full h-full'} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
							{isPlaying ? <path fill="currentColor" d="m37.7035951 30.0915349h7.0265171c.7545686 0 1.3662672.6116986 1.3662672 1.3662672v37.0843957c0 .7545693-.6116986 1.3662672-1.3662672 1.3662672h-7.0265171c-.7545686 0-1.3662672-.6116979-1.3662672-1.3662672v-37.0843957c0-.7545686.6116986-1.3662672 1.3662672-1.3662672zm17.5662927 0h7.0265171c.7545693 0 1.3662672.6116986 1.3662672 1.3662672v37.0843957c0 .7545693-.6116979 1.3662672-1.3662672 1.3662672h-7.0265171c-.7545693 0-1.3662672-.6116979-1.3662672-1.3662672v-37.0843957c0-.7545686.6116979-1.3662672 1.3662672-1.3662672z"/> : <path fill="currentColor" d="m69.2501839 48.4811885c.2623831.1573835.4797618.380239.6304786.6464322.1507123.2664248.229999.567141.229999.8733295 0 .3059523-.0792867.6069047-.229999.8730979-.1507169.2664294-.3680956.4890487-.6304786.6466638l-30.2297592 18.1357157c-.268809.160953-.5754789.24786-.889047.2519063-.3133365.0038102-.6221453-.0754765-.8950006-.2297628-.2726192-.1545225-.4995255-.3785724-.6576197-.649048-.1578581-.2707118-.241191-.5785715-.2414295-.8919035v-36.2750008c.0002384-.3135704.0835714-.6211894.2414295-.8919057.1580942-.2707141.3850006-.4947617.6576197-.6490458.2728553-.1545248.5816641-.2338114.8950006-.2300013.3135681.0040486.620238.0909533.889047.2519063z"/>}
							<circle cx="50" cy="50" r="47.5" className="stroke-black/20" fill='none' strokeWidth="5" strokeMiterlimit="10" />
							<circle cx="50" cy="50" r="47.5" className="stroke-black" fill='none' strokeWidth="5" strokeMiterlimit="10" transform="rotate(-90 50 50)" style={{
								strokeDasharray: 2 * Math.PI * 47.5,
								strokeDashoffset: (!timeProgress || !duration) ? 2 * Math.PI * 47.5 : 2 * Math.PI * 47.5 - (2 * Math.PI * 47.5 * (timeProgress / duration))
							}} />
						</svg>

					</button>

				</div>

				{/* <p>Playing: {isPlaying ? "true" : "false"}</p>
				<p>Progress: {timeProgress / duration}</p> */}

			</div>

		</>
	)
}
export default AudioPlayer
